import React from 'react';
import { FiAlertCircle, FiRefreshCw, FiHome, FiDownload } from 'react-icons/fi';
import logger from '../../utils/logging/logger';
import { isChunkLoadError } from './errorBoundaryHelpers';
import {
  ErrorContainer, ErrorContent, ErrorIcon, ErrorTitle, ErrorMessage, ErrorDetails,
  ButtonGroup, Button, UpdateContainer, UpdateContent, UpdateIcon, UpdateTitle,
  UpdateMessage, CountdownText, UpdateButton
} from './ErrorBoundary.styles';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      isChunkError: false,
      autoRefreshCountdown: 10 // Seconds until auto-refresh for chunk errors
    };
    this.countdownInterval = null;
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    // Also check if it's a chunk loading error
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error
    logger.error('Error caught by boundary', { error, errorInfo });

    const isChunkErr = isChunkLoadError(error);

    // Log to error tracking service (if available)
    if (window.Sentry || window.Rollbar) {
      const errorService = window.Sentry || window.Rollbar;
      errorService.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        },
        tags: {
          isChunkError: isChunkErr
        }
      });
    }

    // Store error details in state
    this.setState({
      error,
      errorInfo,
      errorCount: this.state.errorCount + 1,
      isChunkError: isChunkErr
    });

    // For chunk errors, start auto-refresh countdown
    if (isChunkErr) {
      console.log('🔄 Chunk loading error detected - app update required');
      this.startAutoRefreshCountdown();
    } else {
      // Optionally send error to backend for logging (not for chunk errors)
      this.logErrorToBackend(error, errorInfo);
    }
  }

  componentWillUnmount() {
    // Clear countdown interval when component unmounts
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  startAutoRefreshCountdown = () => {
    // Clear any existing interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Start countdown
    this.countdownInterval = setInterval(() => {
      this.setState(prevState => {
        const newCountdown = prevState.autoRefreshCountdown - 1;
        if (newCountdown <= 0) {
          clearInterval(this.countdownInterval);
          // Force refresh with cache clear
          this.handleForceRefresh();
          return { autoRefreshCountdown: 0 };
        }
        return { autoRefreshCountdown: newCountdown };
      });
    }, 1000);
  };

  async logErrorToBackend(error, errorInfo) {
    try {
      const apiService = (await import('../../services/api')).default;
      // FIX: Remove /api prefix since baseURL already includes /portal/api
      await apiService.request('/log-error', {
        method: 'POST',
        body: JSON.stringify({
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name
          },
          errorInfo: {
            componentStack: errorInfo.componentStack
          },
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        })
      });
    } catch (logError) {
      // Silently fail - don't break the error boundary
      logger.warn('Failed to log error to backend', { error: logError });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  handleForceRefresh = async () => {
    console.log('🔄 Force refreshing app to get latest version...');

    try {
      // Clear all caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Browser caches cleared');
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('✅ Service workers unregistered');
      }
    } catch (error) {
      console.warn('Cache clearing failed, proceeding with reload:', error);
    }

    // Force reload without cache
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      // Special UI for chunk loading errors (app update required)
      if (this.state.isChunkError) {
        return (
          <UpdateContainer>
            <UpdateContent>
              <UpdateIcon>
                <FiDownload />
              </UpdateIcon>
              <UpdateTitle>App Update Required</UpdateTitle>
              <UpdateMessage>
                A new version of the application has been deployed. Please refresh to get the latest
                version with new features and bug fixes.
              </UpdateMessage>

              <UpdateButton onClick={this.handleForceRefresh}>
                <FiRefreshCw />
                Refresh Now
              </UpdateButton>

              <CountdownText>
                Auto-refreshing in {this.state.autoRefreshCountdown} seconds...
              </CountdownText>
            </UpdateContent>
          </UpdateContainer>
        );
      }

      // Custom fallback UI for other errors
      return (
        <ErrorContainer>
          <ErrorContent>
            <ErrorIcon>
              <FiAlertCircle />
            </ErrorIcon>
            <ErrorTitle>Something went wrong</ErrorTitle>
            <ErrorMessage>
              We're sorry, but something unexpected happened. Don't worry, your data is safe.
              {this.state.errorCount > 1 && (
                <span> This error has occurred {this.state.errorCount} times.</span>
              )}
            </ErrorMessage>

            {this.state.error && process.env.NODE_ENV === 'development' && (
              <ErrorDetails>
                <summary>Error Details (Development Only)</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </ErrorDetails>
            )}

            <ButtonGroup>
              <Button onClick={this.handleReset}>
                <FiRefreshCw />
                Try Again
              </Button>
              <Button primary onClick={this.handleGoHome}>
                <FiHome />
                Go Home
              </Button>
              <Button onClick={this.handleReload}>
                <FiRefreshCw />
                Reload Page
              </Button>
            </ButtonGroup>
          </ErrorContent>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
